/**
 * Cat Detail Page
 * Full cat/kitten view with gallery, details, pedigree, and enquiry form
 */

import { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { 
  Heart,
  Share2,
  MapPin,
  Calendar,
  Award,
  Shield,
  ChevronLeft,
  ChevronRight,
  Phone,
  Mail,
  MessageSquare,
  Check,
  X,
  ExternalLink,
  Cat
} from 'lucide-react';
import { Card, Button, Badge, Alert } from '../components/shared/UIComponents';
import { EnquiryForm } from '../components/buyer/BuyerDashboard';
import { formatCurrency, formatDate, getCatAge, cn } from '../utils/helpers';
import { useAuth } from '../hooks/useAuth';
import { 
  doc, 
  getDoc,
  collection,
  addDoc,
  deleteDoc,
  query,
  where,
  getDocs,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';

export const CatDetailPage = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const showEnquiry = searchParams.get('enquire') === 'true';
  
  const { user, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState(null);
  const [cattery, setCattery] = useState(null);
  const [isFavorite, setIsFavorite] = useState(false);
  const [favoriteId, setFavoriteId] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showEnquiryForm, setShowEnquiryForm] = useState(showEnquiry);

  useEffect(() => {
    loadData();
  }, [id]);

  useEffect(() => {
    if (user?.uid && cat?.id) {
      checkFavorite();
    }
  }, [user?.uid, cat?.id]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Try cats collection first
      let catDoc = await getDoc(doc(db, 'cats', id));
      let isKitten = false;
      
      // If not found, try kittens collection
      if (!catDoc.exists()) {
        catDoc = await getDoc(doc(db, 'kittens', id));
        isKitten = true;
      }

      if (!catDoc.exists()) {
        setCat(null);
        return;
      }

      const catData = { id: catDoc.id, ...catDoc.data(), isKitten };
      setCat(catData);

      // Load cattery info
      if (catData.catteryId) {
        const catteryDoc = await getDoc(doc(db, 'catteries', catData.catteryId));
        if (catteryDoc.exists()) {
          setCattery({ id: catteryDoc.id, ...catteryDoc.data() });
        }
      }

    } catch (error) {
      console.error('Error loading cat:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkFavorite = async () => {
    try {
      const favQuery = query(
        collection(db, 'favorites'),
        where('userId', '==', user.uid),
        where(cat.isKitten ? 'kittenId' : 'catId', '==', cat.id)
      );
      const favSnap = await getDocs(favQuery);
      if (!favSnap.empty) {
        setIsFavorite(true);
        setFavoriteId(favSnap.docs[0].id);
      }
    } catch (error) {
      console.error('Error checking favorite:', error);
    }
  };

  const toggleFavorite = async () => {
    if (!isAuthenticated) {
      // Redirect to login
      window.location.href = `/login?redirect=/cat/${id}`;
      return;
    }

    try {
      if (isFavorite && favoriteId) {
        await deleteDoc(doc(db, 'favorites', favoriteId));
        setIsFavorite(false);
        setFavoriteId(null);
      } else {
        const favRef = await addDoc(collection(db, 'favorites'), {
          userId: user.uid,
          catId: cat.isKitten ? null : cat.id,
          kittenId: cat.isKitten ? cat.id : null,
          createdAt: serverTimestamp()
        });
        setIsFavorite(true);
        setFavoriteId(favRef.id);
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  const shareLink = () => {
    if (navigator.share) {
      navigator.share({
        title: `${cat.name} - ${cat.breed}`,
        text: `Check out this beautiful ${cat.breed} on Feline-SA!`,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="animate-pulse">
          <div className="h-96 bg-gray-200 rounded-2xl mb-6" />
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-4" />
          <div className="h-4 bg-gray-200 rounded w-1/4" />
        </div>
      </div>
    );
  }

  if (!cat) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-16 text-center">
        <Cat className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Cat Not Found</h1>
        <p className="text-gray-500 mb-6">This cat may have been removed or adopted.</p>
        <Link to="/marketplace">
          <Button>Browse Available Cats</Button>
        </Link>
      </div>
    );
  }

  const images = cat.photos?.length > 0 ? cat.photos : [
    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 400'%3E%3Crect fill='%23f3f4f6' width='400' height='400'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%23d1d5db' font-family='system-ui' font-size='72'%3E🐱%3C/text%3E%3C/svg%3E"
  ];

  const statusColors = {
    available: 'bg-paw-500 text-white',
    reserved: 'bg-amber-500 text-white',
    sold: 'bg-gray-500 text-white'
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Breadcrumb */}
      <nav className="mb-6 text-sm">
        <Link to="/marketplace" className="text-gray-500 hover:text-feline-600">Marketplace</Link>
        <span className="mx-2 text-gray-300">/</span>
        <span className="text-gray-900">{cat.name}</span>
      </nav>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column - Images & Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Main Image */}
          <div className="relative aspect-[4/3] rounded-2xl overflow-hidden bg-gray-100">
            <img 
              src={images[currentImageIndex]} 
              alt={cat.name}
              className="w-full h-full object-cover"
            />
            
            {/* Status Badge */}
            <div className="absolute top-4 left-4">
              <Badge className={cn('text-sm px-4 py-1', statusColors[cat.status])}>
                {cat.status === 'available' ? 'Available' : 
                 cat.status === 'reserved' ? 'Reserved' : 'Sold'}
              </Badge>
            </div>

            {/* Action Buttons */}
            <div className="absolute top-4 right-4 flex gap-2">
              <button
                onClick={toggleFavorite}
                className={cn(
                  'w-10 h-10 rounded-full flex items-center justify-center shadow-lg transition-all',
                  isFavorite ? 'bg-pink-500 text-white' : 'bg-white text-gray-600 hover:text-pink-500'
                )}
              >
                <Heart className="w-5 h-5" fill={isFavorite ? 'currentColor' : 'none'} />
              </button>
              <button
                onClick={shareLink}
                className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-lg text-gray-600 hover:text-feline-600"
              >
                <Share2 className="w-5 h-5" />
              </button>
            </div>

            {/* Image Navigation */}
            {images.length > 1 && (
              <>
                <button
                  onClick={() => setCurrentImageIndex(i => i === 0 ? images.length - 1 : i - 1)}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 rounded-full flex items-center justify-center text-gray-700 hover:bg-white"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                  onClick={() => setCurrentImageIndex(i => i === images.length - 1 ? 0 : i + 1)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 rounded-full flex items-center justify-center text-gray-700 hover:bg-white"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </>
            )}
          </div>

          {/* Thumbnails */}
          {images.length > 1 && (
            <div className="flex gap-2 overflow-x-auto pb-2">
              {images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentImageIndex(i)}
                  className={cn(
                    'w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 border-2 transition-all',
                    currentImageIndex === i ? 'border-feline-500' : 'border-transparent opacity-60 hover:opacity-100'
                  )}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}

          {/* Details Card */}
          <Card>
            <h1 className="text-2xl font-display font-bold text-gray-900 mb-2">{cat.name}</h1>
            <p className="text-lg text-gray-600 mb-4">{cat.breed}</p>

            {/* Quick Info Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 py-4 border-y border-gray-200">
              <div>
                <p className="text-sm text-gray-500">Sex</p>
                <p className="font-medium text-gray-900">
                  {cat.sex === 'male' ? 'Male ♂' : 'Female ♀'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Age</p>
                <p className="font-medium text-gray-900">{getCatAge(cat.dateOfBirth)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Color</p>
                <p className="font-medium text-gray-900">{cat.color || 'Not specified'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">EMS Code</p>
                <p className="font-medium text-gray-900">{cat.ems || 'N/A'}</p>
              </div>
            </div>

            {/* Description */}
            {cat.description && (
              <div className="py-4">
                <h3 className="font-semibold text-gray-900 mb-2">About {cat.name}</h3>
                <p className="text-gray-600">{cat.description}</p>
              </div>
            )}

            {/* Health Info */}
            <div className="py-4 border-t border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-3">Health & Care</h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center gap-2">
                  {cat.health?.vaccinated ? (
                    <Check className="w-5 h-5 text-paw-500" />
                  ) : (
                    <X className="w-5 h-5 text-gray-300" />
                  )}
                  <span className={cat.health?.vaccinated ? 'text-gray-900' : 'text-gray-400'}>
                    Vaccinated
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {cat.health?.neutered ? (
                    <Check className="w-5 h-5 text-paw-500" />
                  ) : (
                    <X className="w-5 h-5 text-gray-300" />
                  )}
                  <span className={cat.health?.neutered ? 'text-gray-900' : 'text-gray-400'}>
                    Neutered/Spayed
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {cat.microchip ? (
                    <Check className="w-5 h-5 text-paw-500" />
                  ) : (
                    <X className="w-5 h-5 text-gray-300" />
                  )}
                  <span className={cat.microchip ? 'text-gray-900' : 'text-gray-400'}>
                    Microchipped
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {cat.registrationNumber ? (
                    <Check className="w-5 h-5 text-paw-500" />
                  ) : (
                    <X className="w-5 h-5 text-gray-300" />
                  )}
                  <span className={cat.registrationNumber ? 'text-gray-900' : 'text-gray-400'}>
                    Registered
                  </span>
                </div>
              </div>
            </div>

            {/* Registration */}
            {cat.registrationNumber && (
              <div className="py-4 border-t border-gray-200">
                <div className="flex items-center gap-2 text-paw-600">
                  <Award className="w-5 h-5" />
                  <span className="font-medium">Registration: {cat.registrationNumber}</span>
                </div>
              </div>
            )}
          </Card>
        </div>

        {/* Right Column - Price, Breeder, Enquiry */}
        <div className="space-y-6">
          {/* Price Card */}
          <Card className="bg-gradient-to-br from-feline-50 to-paw-50">
            {cat.price ? (
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-1">Price</p>
                <p className="text-4xl font-bold text-feline-600">{formatCurrency(cat.price)}</p>
              </div>
            ) : (
              <div className="text-center">
                <p className="text-lg text-gray-600">Price on Enquiry</p>
              </div>
            )}

            {cat.status === 'available' && (
              <Button 
                className="w-full mt-4" 
                size="lg"
                onClick={() => setShowEnquiryForm(true)}
              >
                <MessageSquare className="w-5 h-5" />
                Enquire Now
              </Button>
            )}

            {cat.status === 'reserved' && (
              <Alert type="warning" className="mt-4">
                This cat is currently reserved
              </Alert>
            )}
          </Card>

          {/* Cattery Card */}
          {cattery && (
            <Card>
              <h3 className="font-semibold text-gray-900 mb-4">Breeder</h3>
              
              <div className="flex items-center gap-4 mb-4">
                <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center">
                  {cattery.logoUrl ? (
                    <img src={cattery.logoUrl} alt="" className="w-full h-full object-cover rounded-full" />
                  ) : (
                    <span className="text-2xl">🐱</span>
                  )}
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{cattery.catteryName}</p>
                  <p className="text-sm text-gray-500">{cattery.ownerName}</p>
                </div>
              </div>

              <div className="space-y-2 mb-4">
                <p className="flex items-center gap-2 text-sm text-gray-600">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  {cattery.city}, {cattery.province}
                </p>
                {cattery.establishedYear && (
                  <p className="flex items-center gap-2 text-sm text-gray-600">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    Established {cattery.establishedYear}
                  </p>
                )}
                {cattery.registrations?.length > 0 && (
                  <p className="flex items-center gap-2 text-sm text-gray-600">
                    <Shield className="w-4 h-4 text-gray-400" />
                    {cattery.registrations.join(', ')} registered
                  </p>
                )}
              </div>

              {/* Contact Buttons */}
              <div className="space-y-2">
                {cattery.phone && (
                  <a href={`tel:${cattery.phone}`} className="flex items-center justify-center gap-2 w-full py-2 px-4 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors">
                    <Phone className="w-4 h-4" />
                    {cattery.phone}
                  </a>
                )}
                {cattery.whatsapp && (
                  <a 
                    href={`https://wa.me/${cattery.whatsapp.replace(/\D/g, '')}?text=Hi, I'm interested in ${cat.name} on Feline-SA`}
                    target="_blank"
                    className="flex items-center justify-center gap-2 w-full py-2 px-4 bg-paw-500 text-white rounded-xl hover:bg-paw-600 transition-colors"
                  >
                    <MessageSquare className="w-4 h-4" />
                    WhatsApp
                  </a>
                )}
              </div>

              {cattery.website && (
                <a 
                  href={cattery.website}
                  target="_blank"
                  className="flex items-center justify-center gap-2 text-sm text-feline-600 hover:underline mt-4"
                >
                  Visit Website
                  <ExternalLink className="w-4 h-4" />
                </a>
              )}
            </Card>
          )}

          {/* Enquiry Form */}
          {showEnquiryForm && cat.status === 'available' && cattery && (
            <Card>
              <EnquiryForm 
                cat={cat}
                cattery={cattery}
                onSuccess={() => setShowEnquiryForm(false)}
              />
            </Card>
          )}

          {/* Safety Tips */}
          <Card className="bg-amber-50 border-amber-200">
            <h4 className="font-semibold text-amber-800 mb-2">🛡️ Safety Tips</h4>
            <ul className="text-sm text-amber-700 space-y-1">
              <li>• Always visit the cattery before paying</li>
              <li>• Ask to see health certificates</li>
              <li>• Get a written sale contract</li>
              <li>• Don't pay full amount upfront</li>
            </ul>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CatDetailPage;
